import { ReactNode, useMemo, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable, DataTableSortEvent } from 'primereact/datatable'
import { ProgressSpinner } from 'primereact/progressspinner'
import { Tag } from 'primereact/tag'
import InfoTip from '../../../components/novarium/InfoTip'
import { OperatingRoomRoomDTO, OperatingRoomRoomsDTO } from '../../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { SectionState } from '../../UrgencySummary/useSectionData'
import {
  formatCount,
  formatDuration,
  formatHoursPlain,
  formatPercent,
  NO_VALUE,
} from '../operatingRoomFormat'

type SortField =
  | 'name' | 'surgeries' | 'urgent' | 'scheduledPct' | 'hoursUsed' | 'capacityHours' | 'insideBlockHours' | 'outsideBlockHours'
  | 'occupancyPct' | 'medianDurationMinutes' | 'annulled' | 'rescheduled' | 'missed'

interface SortState {
  field: SortField
  order: 1 | -1
}

/** Las salas con más cirugías, primero (como llegan del backend). */
const DEFAULT_SORT: SortState = { field: 'surgeries', order: -1 }

function compareRooms(a: OperatingRoomRoomDTO, b: OperatingRoomRoomDTO, sort: SortState): number {
  if (sort.field === 'name') return sort.order * (a.name ?? '').localeCompare(b.name ?? '', 'es')
  const left = a[sort.field]
  const right = b[sort.field]
  // Sin dato, siempre al final
  if (left === null && right === null) return 0
  if (left === null) return 1
  if (right === null) return -1
  return sort.order * (left - right)
}

/** «40 · 33,3 %»: la cantidad y su parte del total de la fila. */
const countWithShare = (intl: IntlShape, count: number, pct: number | null): string =>
  `${formatCount(intl, count)} · ${formatPercent(intl, pct)}`

interface OperatingRoomRoomsProps {
  rooms: SectionState<OperatingRoomRoomsDTO>
  /** Una sola acción por fila: ver las cirugías de la sala en la lista (filtra la pantalla por esa sala). */
  onDrill: (room: OperatingRoomRoomDTO) => void
}

/**
 * Una fila por quirófano, con el total del período al pie (el mismo de los indicadores): cirugías, programadas (con agenda) y
 * urgentes, horas utilizadas, la capacidad (horas de agenda de la sala), lo usado dentro y fuera de agenda, la ocupación, la
 * duración mediana y las tres cuentas de lo que no se hizo (anuladas, reprogramadas y ausentes, nunca sumadas). Una sala sin
 * agenda vigente lleva su marca y la ocupación «—»; sus horas cuentan igual, como fuera de agenda. Completa en memoria: el
 * orden por columna se resuelve acá.
 */
export default function OperatingRoomRooms({ rooms, onDrill }: OperatingRoomRoomsProps) {
  const intl = useIntl()
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
  const loaded = rooms.data ?? rooms.stale
  const refreshing = rooms.status === 'loading'
  const total = loaded?.total ?? null
  const rows = useMemo(() => [...(loaded?.rooms ?? [])].sort((a, b) => compareRooms(a, b, sort)), [loaded, sort])
  const centered = { className: 'nv-urg-center', headerClassName: 'nv-urg-center' }

  const header = (id: string, hintId?: string): ReactNode => (
    <span className="nv-or-th">
      {intl.formatMessage({ id })}
      {hintId && <InfoTip text={intl.formatMessage({ id: hintId })} />}
    </span>
  )

  return (
    <section className="nv-or-group" aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsTabRooms' })} aria-busy={refreshing}>
      <div className="nv-or-group__body">
        {refreshing && loaded === null && (
          <span className="nv-loading" role="status">
            <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
            <span>{intl.formatMessage({ id: 'OperatingRoomIndicatorsLoading' })}</span>
          </span>
        )}
        {rooms.status === 'failed' && (
          <div className="nv-or-failed" role="alert">
            <p className="nv-note">
              <i className="pi pi-info-circle" aria-hidden="true" />
              {intl.formatMessage({ id: 'OperatingRoomIndicatorsRoomsError' })}
            </p>
            <Button type="button" link label={intl.formatMessage({ id: 'OperatingRoomIndicatorsRetry' })} onClick={rooms.retry} />
          </div>
        )}
        {loaded !== null && rooms.status !== 'failed' && (rows.length === 0
          ? <p className="nv-urg-empty">{intl.formatMessage({ id: 'OperatingRoomIndicatorsEmpty' })}</p>
          : (
            <div className={`nv-load-table${refreshing ? ' nv-or-blurred' : ''}`}>
              <DataTable
                value={rows}
                dataKey="roomId"
                lazy
                sortField={sort.field}
                sortOrder={sort.order}
                onSort={(event: DataTableSortEvent) => setSort({ field: event.sortField as SortField, order: event.sortOrder === 1 ? 1 : -1 })}
              >
                <Column
                  field="name"
                  header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColRoom' })}
                  sortable
                  footer={total ? intl.formatMessage({ id: 'OperatingRoomIndicatorsTotal' }) : undefined}
                  body={(room: OperatingRoomRoomDTO) => (
                    <span className="nv-or-name">
                      <span>{room.name}</span>
                      {!room.hasTemplate && <Tag className="nv-tag-neutral" value={intl.formatMessage({ id: 'OperatingRoomIndicatorsNoTemplateTag' })} />}
                    </span>
                  )}
                />
                <Column
                  field="surgeries"
                  header={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiSurgeries' })}
                  sortable
                  {...centered}
                  footer={total ? formatCount(intl, total.surgeries) : undefined}
                  body={(room: OperatingRoomRoomDTO) => formatCount(intl, room.surgeries)}
                />
                <Column
                  field="scheduledPct"
                  header={header('OperatingRoomIndicatorsKpiScheduled', 'OperatingRoomIndicatorsKpiScheduledHint')}
                  sortable
                  {...centered}
                  footer={total ? countWithShare(intl, total.scheduled, total.scheduledPct) : undefined}
                  body={(room: OperatingRoomRoomDTO) => countWithShare(intl, room.scheduled, room.scheduledPct)}
                />
                <Column
                  field="urgent"
                  header={header('OperatingRoomIndicatorsKpiUrgent', 'OperatingRoomIndicatorsKpiUrgentHint')}
                  sortable
                  {...centered}
                  footer={total ? countWithShare(intl, total.urgent, total.urgentPct) : undefined}
                  body={(room: OperatingRoomRoomDTO) => countWithShare(intl, room.urgent, room.urgentPct)}
                />
                <Column
                  field="hoursUsed"
                  header={header('OperatingRoomIndicatorsColHours', 'OperatingRoomIndicatorsKpiHoursHint')}
                  sortable
                  {...centered}
                  footer={total ? formatHoursPlain(intl, total.hoursUsed) : undefined}
                  body={(room: OperatingRoomRoomDTO) => formatHoursPlain(intl, room.hoursUsed)}
                />
                <Column
                  field="capacityHours"
                  header={header('OperatingRoomIndicatorsColCapacity', 'OperatingRoomIndicatorsColCapacityHint')}
                  sortable
                  {...centered}
                  footer={total ? formatHoursPlain(intl, total.capacityHours) : undefined}
                  body={(room: OperatingRoomRoomDTO) => (room.hasTemplate ? formatHoursPlain(intl, room.capacityHours) : NO_VALUE)}
                />
                <Column
                  field="insideBlockHours"
                  header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColInside' })}
                  sortable
                  {...centered}
                  footer={total ? formatHoursPlain(intl, total.insideBlockHours) : undefined}
                  body={(room: OperatingRoomRoomDTO) => formatHoursPlain(intl, room.insideBlockHours)}
                />
                <Column
                  field="outsideBlockHours"
                  header={header('OperatingRoomIndicatorsColOutside', 'OperatingRoomIndicatorsColOutsideHint')}
                  sortable
                  {...centered}
                  footer={total ? `${formatHoursPlain(intl, total.outsideBlockHours)} · ${formatPercent(intl, total.outsideBlockPct)}` : undefined}
                  body={(room: OperatingRoomRoomDTO) => `${formatHoursPlain(intl, room.outsideBlockHours)} · ${formatPercent(intl, room.outsideBlockPct)}`}
                />
                <Column
                  field="occupancyPct"
                  header={header('OperatingRoomIndicatorsKpiOccupancy', 'OperatingRoomIndicatorsKpiOccupancyHint')}
                  sortable
                  {...centered}
                  footer={total ? formatPercent(intl, total.occupancyPct) : undefined}
                  body={(room: OperatingRoomRoomDTO) => formatPercent(intl, room.occupancyPct)}
                />
                <Column
                  field="medianDurationMinutes"
                  header={intl.formatMessage({ id: 'OperatingRoomIndicatorsColMedian' })}
                  sortable
                  {...centered}
                  footer={total ? formatDuration(intl, total.medianDurationMinutes) : undefined}
                  body={(room: OperatingRoomRoomDTO) => formatDuration(intl, room.medianDurationMinutes)}
                />
                <Column
                  field="annulled"
                  header={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiAnnulled' })}
                  sortable
                  {...centered}
                  footer={total ? formatCount(intl, total.annulled) : undefined}
                  body={(room: OperatingRoomRoomDTO) => formatCount(intl, room.annulled)}
                />
                <Column
                  field="rescheduled"
                  header={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiRescheduled' })}
                  sortable
                  {...centered}
                  footer={total ? formatCount(intl, total.rescheduled) : undefined}
                  body={(room: OperatingRoomRoomDTO) => formatCount(intl, room.rescheduled)}
                />
                <Column
                  field="missed"
                  header={intl.formatMessage({ id: 'OperatingRoomIndicatorsKpiMissed' })}
                  sortable
                  {...centered}
                  footer={total ? formatCount(intl, total.missed) : undefined}
                  body={(room: OperatingRoomRoomDTO) => formatCount(intl, room.missed)}
                />
                <Column
                  header={<span className="nv-sr-only">{intl.formatMessage({ id: 'OperatingRoomIndicatorsViewSurgeries' })}</span>}
                  className="nv-col-actions"
                  headerClassName="nv-col-actions"
                  body={(room: OperatingRoomRoomDTO) => (
                    // TODO(design-system): el ojo (ver detalle) abre acá la lista filtrada, no un detalle: no hay un ícono fijo para «ver en la lista».
                    <Button
                      type="button"
                      text
                      className="nv-icon nv-tip"
                      icon="pi pi-eye"
                      aria-label={intl.formatMessage({ id: 'OperatingRoomIndicatorsViewSurgeriesOf' }, { name: room.name ?? '' })}
                      data-pr-tooltip={intl.formatMessage({ id: 'OperatingRoomIndicatorsViewSurgeries' })}
                      onClick={() => onDrill(room)}
                    />
                  )}
                />
              </DataTable>
            </div>
          ))}
      </div>
    </section>
  )
}
